import { Avatar, Button, Checkbox, ConfigProvider, Form, Input, message, Modal, Skeleton, Upload } from 'antd';
import {
  getCurrentlyActiveOrganization,
  removeActiveOrganization,
  setCurrentlyActiveOrganization,
} from '../../../common/auth';
import {
  Organization,
  triggerOrganizationDeletion,
  updateOrganizationMetadata,
  uploadOrganizationPicture,
} from '../../../api/organization';
import { useEffect, useState } from 'react';
import ImgCrop from 'antd-img-crop';
import { CloseOutlined, LoadingOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetOrganization, useUpdateOrganizationMutation } from '../../../serverState/organization';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import { CustomSecondaryButton } from '../../../components/UI/CustomSecondaryButton';
import CustomInputLabel from '../../../components/UI/CustomInputLabel';
import { useNavigate } from 'react-router-dom';
import paths from '../../../router/RoutePaths';
import { queryClient } from '../../../queryClient';
import colorTokens from '../../../constants/design_system/colors';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';

function OrganizationSettings() {
  // Import message form antd
  const [messageApi, contextHolder] = message.useMessage();

  const [org, setOrg] = useState<Organization | null>();
  // Show Avatar with organization-first letter as fallback

  const [updatedOrgLogo, setUpdatedOrgLogo] = useState<{ id: number | null; path: string | null } | undefined>(
    undefined
  );

  const [editOrgProfileModal, setEditOrgProfileModal] = useState<boolean>(false);

  useEffect(() => {
    getCurrentlyActiveOrganization()
      .then((org) => {
        setOrg(org);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const [deleteOrgModal, setDeleteOrgModal] = useState<boolean>(false);

  interface EditOrgProfileFormValues {
    name: string;
  }

  const [editOrgProfileForm] = Form.useForm<EditOrgProfileFormValues>();

  const handleUpload = async (uploadRequest: unknown) => {
    const req = uploadRequest as { file: Blob; filename: string };
    if (!req.file || !req.filename) {
      return;
    }
    // Set multipart form data
    const formData = new FormData();

    // Add file to form data
    formData.append(req.filename, req.file, req.file.name);

    // setUploading(true);

    try {
      const uploadedPhoto = (await uploadOrganizationPicture(org?.id || 0, req.file)).data.data;
      setUpdatedOrgLogo({ id: uploadedPhoto.id, path: uploadedPhoto.path });
    } catch (e) {
      console.log(e);
    }
  };

  const beforeUpload = (file: any) => {
    console.log(file);
  };

  const handleChange = (info: any) => {
    console.log(info);
  };

  const [orgLogoImgUrl, setOrgLogoImgUrl] = useState<string>();
  const [loadingOrgLogo, setLoadingOrgLogo] = useState(false);

  const [organizatioNameConfirm, setOrganizationNameConfirm] = useState<string>('');

  interface OrganizationDeleteFormValues {
    organizationNameConfirm: string;
  }

  const [organizationDeleteForm] = Form.useForm<OrganizationDeleteFormValues>();

  const navigate = useNavigate();

  const handleOrganizationDelete = async () => {
    // Assumes that the form has done its validation

    // Skip in case of empty org being set
    if (!org?.id) {
      return;
    }

    // Trigger an API call to start the deletion process
    try {
      const res = await triggerOrganizationDeletion(org.id);
      void messageApi.success('Organization deletion process has been started');

      await queryClient.invalidateQueries({
        queryKey: ['usersOrganizationAccessAndInvites'],
      });
      await removeActiveOrganization();
      navigate(paths.LoginSelectOrganization);
    } catch (e) {
      console.log(e);
      void messageApi.error('Something went wrong while triggering the organization deletion process');
    }
  };

  const handleOrganizationUpdate = async () => {
    // Assumes that the form has done its validation
    const values = await editOrgProfileForm.validateFields();

    // Skip in case of empty org being set
    if (!org?.id) {
      return;
    }

    // Trigger an API call to update the org
    try {
      // Trigger update
      const res = await updateOrganizationMetadata(org.id, values.name, updatedOrgLogo);

      // Update tokens
      const updateCurrentlyActiveOrganization = await setCurrentlyActiveOrganization(res.data.organizationWithAccess);

      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      // Update the org object in the state
      setOrg({
        ...org,
        name: values.name,
        organizationPicturePath: updatedOrgLogo?.path,
      });

      void messageApi.success('Organization updated successfully');

      setEditOrgProfileModal(false);
    } catch (e) {
      console.log(e);
      void messageApi.error('Something went wrong while updating the organization');
    }
  };

  const organizationQuery = useGetOrganization(org?.id as number);

  const actualPath = getUserProfilePictureUrl(organizationQuery.data?.organizationPicturePath);

  const [confirmDeleteCheckboxValue, setconfirmDeleteCheckboxValue] = useState(false);

  const updateOrgMutation = useUpdateOrganizationMutation();

  return (
    <div>
      {contextHolder}
      <div className={'px-12 max-w-5xl mx-auto w-full'}>
        <div className="text-brand-grey-dark text-xl font-semibold leading-7 mb-6 mt-8">Organization Settings</div>

        <div className="flex flex-col gap-3">
          <div className="rounded-lg border border-gray-300 p-4 border-solid custom-card">
            <div className="text-brand-grey-dark text-base font-semibold leading-normal">Organization Information</div>

            <div className={'flex items-center'}>
              <div className="profile-logo">
                <Avatar className={'mr-2'} src={actualPath}>
                  {org ? org?.name.charAt(0) : <Skeleton />}
                </Avatar>
              </div>
              <div className="profile-name-email pl-2 flex flex-col gap-1">
                <div className="profile-name font-bold">{org ? org.name : <Skeleton />}</div>
              </div>
              <div className="action-items grow align-middle flex justify-end">
                <CustomSecondaryButton type="default" onClick={() => setEditOrgProfileModal(true)}>
                  Edit
                </CustomSecondaryButton>
              </div>
            </div>
          </div>

          <div className="h-px bg-neutral-300 rounded-sm my-3"></div>

          <div className="h-6 justify-start items-start gap-6 inline-flex">
            <div className="px-2 py-0.5 bg-red-100 rounded justify-center items-center gap-2.5 flex">
              <div className="justify-center items-center gap-1 flex">
                <div className="text-red-600 text-sm font-normal leading-tight">Danger</div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-300 p-4 border-solid  custom-card">
            <div className="text-brand-grey-dark text-base font-semibold leading-normal">Delete Organization</div>

            <div className={'flex items-center'}>
              {/* <img src={actualPath} /> */}
              <div className="profile-name-email flex flex-col gap-1">
                <div className="text-brand-grey-dark text-sm font-medium leading-tight">
                  Permanently delete your user account and data, effective immediately.
                </div>
              </div>

              <div className="action-items grow align-middle flex justify-end">
                <CustomSecondaryButton type="delete" onClick={() => setDeleteOrgModal(true)}>
                  Delete Organization
                </CustomSecondaryButton>

                {/* <ConfigProvider theme={{
                  components: {
                    Button: {
                      colorPrimary: colorTokens.red[600]
                    }
                  }
                }}>
                  <Button
                    className="text-red-600 border-red-600"
                    type='default'
                    onClick={() => setDeleteOrgModal(true)}>
                  Delete Organization
                  </Button>
                </ConfigProvider> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title={<div className="w-full text-center"> Edit Organization</div>}
        open={editOrgProfileModal}
        onCancel={() => setEditOrgProfileModal(false)}
        footer={null}
        className=" max-w-[330px] f"
        centered
      >
        <div className="flex flex-col items-stretch justify-around gap-8">
          <div
            // style={{
            //   width: 'min-content',
            //   margin: '0 auto',
            // }}
            className="flex justify-center items-center w-full mt-7"
          >
            <ImgCrop rotationSlider quality={1} zoomSlider>
              <Upload
                name="organization-picture"
                listType="picture-circle"
                className="max-w-min"
                showUploadList={false}
                customRequest={handleUpload}
                beforeUpload={beforeUpload}
                onChange={handleChange}
              >
                {(updatedOrgLogo && updatedOrgLogo.path !== null) || (!updatedOrgLogo && actualPath) ? (
                  <div className="relative">
                    <div
                      className="absolute right-0 bottom-0 bg-gray-50 w-6 h-6 z-10 flex justify-center items-center"
                      style={{ borderRadius: '50%' }}
                      onClick={async (event) => {
                        // alert('works') // remove the image
                        event.stopPropagation();

                        setUpdatedOrgLogo({ id: null, path: null });
                      }}
                    >
                      <CustomTooltip title="Remove the image">
                        <CloseOutlined style={{ color: 'red' }} />
                      </CustomTooltip>
                    </div>
                    <Avatar
                      src={updatedOrgLogo ? getUserProfilePictureUrl(updatedOrgLogo.path) : actualPath}
                      size={100}
                    >
                      {org ? org?.name.charAt(0) : <Skeleton />}
                    </Avatar>
                  </div>
                ) : (
                  <div>
                    {loadingOrgLogo ? <LoadingOutlined /> : <PlusOutlined />}
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </ImgCrop>
          </div>

          <Form
            form={editOrgProfileForm}
            name="edit-org-profile"
            layout={'vertical'}
            initialValues={{
              name: org?.name,
            }}
            onFinish={handleOrganizationUpdate}
          >
            <CustomInputLabel>Organization Name</CustomInputLabel>
            <Form.Item
              // label="Organization Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Please input your organization name!',
                },
              ]}
            >
              <Input type="text" placeholder="Name" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" className="w-full">
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        // title={}
        open={deleteOrgModal}
        onCancel={() => setDeleteOrgModal(false)}
        onOk={handleOrganizationDelete}
        // confirmLoading={confirmLoadingDeleteTask}
        okButtonProps={{ danger: true, disabled: !confirmDeleteCheckboxValue }}
        okText="Delete Organization"
        centered
      >
        <div className="p-8 pt-4 pb-12">
          <h1 className="text-lg text-left font-semibold">{`Delete Organization ${org?.name}`}</h1>
          <div className="flex gap-4 items-start">
            <Checkbox
              value={confirmDeleteCheckboxValue}
              onChange={(event) => {
                setconfirmDeleteCheckboxValue(event.target.checked);
              }}
            ></Checkbox>
            <span className="text-sm text-[#707792] ">
              By checking this box, you acknowledge that all data will be lost upon organization deletion.
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default OrganizationSettings;
